// ProductPicker.tsx — search the catalogue (fast FTS) and pick a product for MANUAL stock entry.
// Shared by the Direct Import "Manual" tab and the Products page "Add stock"/"Add" actions.
import { useEffect, useState, type CSSProperties } from 'react';
import { Chip, Input, mono } from '@/foundation/primitives';
import { useCatalog } from '@/features/master-data/api';
import type { Product } from '@/domain/types';

/** Small level badge so the pharmacist can see they're picking a real, receivable pack:
 *  a dm+d AMPP manufacturer pack or a C+D commercial product — never a virtual family. */
function levelBadge(p: Product): { label: string; color: 'info' | 'warning' } | null {
  if (p.level === 'cnd' || p.source === 'cnd') return { label: 'C+D', color: 'warning' };
  if (p.level === 'ampp' || p.amppCode) return { label: 'AMPP', color: 'info' };
  return null;
}

export function ProductPicker({
  onPick,
  placeholder = 'Search product name…',
}: {
  onPick: (p: Product) => void;
  placeholder?: string;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const active = debounced.length >= 2;
  // This picker is for RECEIVING stock (Direct Import / Add stock / Import-Scan). Stock is always
  // received at the actual pack level, so search ACTUAL products only — dm+d AMPP manufacturer
  // packs + C+D — never a VMPP pack-family. `catalog_kind=products` includes AMPP and excludes VMPP
  // (the backend also rejects a VMPP sku on commit). 50 rows so the specific maker is findable.
  const { data, isFetching } = useCatalog({
    search: active ? debounced : undefined,
    pageSize: 50,
    isActive: true,
    catalogKind: 'products',
  });
  // Belt-and-braces: never offer a VMPP family as a receivable product.
  // Only actual packs are receivable — never a VMP (generic) or VMPP (virtual pack) family; those
  // have no supplier/batch/barcode, so you can't put one on a shelf. Receive at the AMPP/C+D level.
  const products = active
    ? (data?.products ?? []).filter((p) => p.level !== 'vmpp' && p.level !== 'vmp')
    : [];

  return (
    <div style={{ position: 'relative' }}>
      <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={placeholder} />
      {active && (
        <div style={listBox}>
          {isFetching && <div style={hint}>Searching…</div>}
          {!isFetching && products.length === 0 && (
            <div style={hint}>No products match “{debounced}”.</div>
          )}
          {products.map((p) => {
            const badge = levelBadge(p);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onPick(p);
                  setTerm('');
                  setDebounced('');
                }}
                style={rowBtn}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    fontSize: 'var(--fs-body)',
                  }}
                >
                  <span style={{ minWidth: 0, flex: 1 }}>{p.name}</span>
                  {badge && (
                    <Chip color={badge.color} size="s">
                      {badge.label}
                    </Chip>
                  )}
                </div>
                <div
                  style={{
                    ...mono,
                    fontSize: 'var(--fs-caption)',
                    color: 'var(--text-secondary)',
                    marginTop: 2,
                  }}
                >
                  {p.snapshotKey || p.sku}
                  {p.pack ? ` · ${p.pack}` : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const listBox: CSSProperties = {
  marginTop: 6,
  border: '1px solid var(--divider)',
  borderRadius: 'var(--radius-md)',
  maxHeight: 300,
  overflowY: 'auto',
  background: 'var(--bg-card)',
};
const hint: CSSProperties = {
  padding: 14,
  fontSize: 'var(--fs-body)',
  color: 'var(--text-secondary)',
  textAlign: 'center',
};
const rowBtn: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  cursor: 'pointer',
  background: 'transparent',
  padding: '10px 12px',
  borderBottom: '1px solid var(--divider)',
  fontFamily: 'inherit',
};
